import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, Typography } from "@mui/material";
import { DialogTitle } from "@material-ui/core";
import ModalCloseButton from "../../../components/UI/ModalCloseButton";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import StepWizard from "react-step-wizard";
import styles from "../../viewUser/styles/viewUser.module.css";
import statementStyles from "./insuranceStatement.module.css";
import TimeWindowStep, {
  BILLING,
  BILLING_WINDOW_TYPE,
  buildInsuranceStatementPayload,
  getBillingWindowRange,
} from "./steps/TimeWindowStep";
import PatientsStep from "./steps/PatientsStep";
import StatementStep from "./steps/StatementStep";
import useAxiosPrivate from "../../../utils/hooks/useAxiosPrivate";
import { handleApiError } from "../../../utils/lib/apiErrorHandler";

const STEPS = ["Time Window", "Patients", "Statement"];

const footerBtnBase = {
  width: 196,
  height: 48,
  borderRadius: 8,
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "16px",
  cursor: "pointer",
};

function ValidationErrorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="10" fill="#F5222D" />
      <path d="M6 6L14 14M14 6L6 14" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

export default function InsuranceStatementModal({
  open,
  onClose,
  initialStep = 1,
  initialPatients,
  hideSteps = false,
}) {
  const wizardInstance = React.useRef();
  const axios = useAxiosPrivate();

  const getWizardInstance = (instance) => {
    wizardInstance.current = instance;
  };

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [complianceDays, setComplianceDays] = useState(16);
  const [billingWindow, setBillingWindow] = useState(BILLING.LAST_30);
  const [customDays, setCustomDays] = useState(31);
  const [statementPatients, setStatementPatients] = useState(() => initialPatients);
  const [selectedPatientIds, setSelectedPatientIds] = useState(
    () => new Set((initialPatients ?? []).map((p) => p.id))
  );
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState(null);
  const [statementData, setStatementData] = useState({
    condition: "",
    minutes: "",
    additionalMinutes: 0,
  });
  const [rawActiveStatementPatientId, setActiveStatementPatientId] = useState(null);
  const [isStatementFinalized, setIsStatementFinalized] = useState(false);
  // Snapshot of which patients were selected at the moment "Done & Copy" last
  // succeeded — used to tell whether the patient selection has since changed.
  const [finalizedPatientIds, setFinalizedPatientIds] = useState(null);
  // Snapshot of the Time Window settings used for the last successful patients
  // fetch — if the user goes back and returns without changing any of these,
  // we skip re-fetching from the backend and just reuse the existing list.
  const [fetchedTimeWindow, setFetchedTimeWindow] = useState(null);
  const [isSubmittingSummary, setIsSubmittingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  // Shared error toast for all steps — holds the message text (or null when hidden).
  const [showValidationError, setShowValidationError] = useState(null);

  useEffect(() => {
    if (!showValidationError) return;
    const timer = setTimeout(() => setShowValidationError(null), 4000);
    return () => clearTimeout(timer);
  }, [showValidationError]);

  // If the patient selection has diverged from what it was when the statement was
  // last finalized, drop back to editable mode and clear the previously-entered
  // statement text (it no longer reflects the current patient group) — otherwise
  // leave the finalized view as-is (e.g. the user toggled something and toggled
  // it right back).
  useEffect(() => {
    if (!finalizedPatientIds) return;
    const isSameSelection =
      selectedPatientIds.size === finalizedPatientIds.size &&
      [...selectedPatientIds].every((id) => finalizedPatientIds.has(id));
    setIsStatementFinalized(isSameSelection);
    if (!isSameSelection) {
      setStatementData({ condition: "", minutes: "", additionalMinutes: 0 });
    }
  }, [selectedPatientIds, finalizedPatientIds]);

  const handleClose = () => {
    setCurrentStep(initialStep);
    setComplianceDays(16);
    setBillingWindow(BILLING.LAST_30);
    setCustomDays(31);
    setStatementPatients(initialPatients);
    setSelectedPatientIds(new Set((initialPatients ?? []).map((p) => p.id)));
    setFetchedTimeWindow(null);
    setPatientsError(null);
    setStatementData({ condition: "", minutes: "", additionalMinutes: 0 });
    setActiveStatementPatientId(null);
    setIsStatementFinalized(false);
    setFinalizedPatientIds(null);
    setIsSubmittingSummary(false);
    setSummaryError(null);
    setShowValidationError(null);
    onClose();
  };

  const goBack = () => {
    // Whether the statement should still show as finalized after coming back is
    // decided by the patient-selection comparison effect above, not here — going
    // back on its own shouldn't drop out of the finalized view.
    setShowValidationError(null);
    wizardInstance.current?.previousStep();
  };
  const goNext = () => wizardInstance.current?.nextStep();

  // Breadcrumbs can only navigate back to an already-completed step, never
  // forward — jumping ahead would skip the patient fetch (Time Window → Patients)
  // or the "at least one patient selected" requirement (Patients → Statement).
  const goToBreadcrumbStep = (stepNum) => {
    if (stepNum >= currentStep) return;
    setShowValidationError(null);
    wizardInstance.current?.goToStep(stepNum);
  };

  const handleActiveStatementPatientChange = (id) => {
    setActiveStatementPatientId(id);
    setIsStatementFinalized(false);
  };

  const statementStepPatients = (statementPatients ?? []).filter((p) =>
    selectedPatientIds.has(p.id)
  );

  const activeStatementPatientId =
    rawActiveStatementPatientId ?? statementStepPatients[0]?.id ?? null;

  const isStatementComplete =
    statementStepPatients.length > 0 &&
    String(statementData.condition ?? "").trim() !== "" &&
    String(statementData.minutes ?? "").trim() !== "";

  const isNextFromPatientsDisabled = currentStep === 2 && selectedPatientIds.size === 0;

  const toggleAllPatients = () => {
    setSelectedPatientIds((prev) =>
      prev.size === (statementPatients ?? []).length
        ? new Set()
        : new Set((statementPatients ?? []).map((p) => p.id))
    );
  };

  const togglePatient = (id) => {
    setSelectedPatientIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const updateStatementField = (field, value) => {
    setStatementData((prev) => ({ ...prev, [field]: value }));
  };

  const buildStatementText = (fields) => {
    const condition = fields?.condition ?? "";
    const minutes = fields?.minutes ?? "";
    const additionalMinutes = fields?.additionalMinutes ?? "";
    return [
      "1. Initial enrollment",
      `Patient enrolled in remote patient monitoring for ${condition}; consent obtained and RPM education completed (CPT 99453).`,
      "",
      "2. 30-day RPM billing",
      `Device supplied and transmitted readings received for the required days (CPT 99454). Clinical staff reviewed data and spent ${minutes} minutes monitoring and coordinating care this month, including interactive communication with the patient/caregiver (CPT 99457).`,
      "",
      "3. Additional time spent",
      `Additional ${additionalMinutes} minutes of clinical monitoring/care management time were spent this billing period (CPT 99458). Clinical plan updated based on RPM findings.`,
    ].join("\n");
  };

  const formatDate = (date) => date.toISOString().split("T")[0];

  const getBillingWindowDates = () => {
    const { start, end } = getBillingWindowRange({ billingWindow, customDays });
    return { window_start_date: formatDate(start), window_end_date: formatDate(end) };
  };

  const buildInsuranceStatementSummaryPayload = () => {
    const activePatient =
      statementStepPatients.find((p) => p.id === activeStatementPatientId) ??
      statementStepPatients[0];

    return {
      patient_id: statementStepPatients.map((p) => p.id),
      billing_summary_text: buildStatementText(statementData),
      condition: statementData.condition,
      monitoring_minutes: Number(statementData.minutes) || 0,
      additional_minutes: Number(statementData.additionalMinutes) || 0,
      billing_window_type: BILLING_WINDOW_TYPE[billingWindow],
      ...getBillingWindowDates(),
      used_days: activePatient?.usedDays ?? 0,
      compliance_threshold: complianceDays,
    };
  };

  const handleDoneAndCopy = async () => {
    if (!isStatementComplete) {
      setShowValidationError("Please complete all the mandatory fields.");
      return;
    }
    setShowValidationError(null);
    setSummaryError(null);
    setIsSubmittingSummary(true);
    try {
      await axios.post("/insurance-statement/summaries", buildInsuranceStatementSummaryPayload());
      navigator.clipboard?.writeText(buildStatementText(statementData)).catch(() => {});
      setIsStatementFinalized(true);
      setFinalizedPatientIds(new Set(selectedPatientIds));
    } catch (error) {
      setSummaryError(handleApiError(error).message);
    } finally {
      setIsSubmittingSummary(false);
    }
  };

  const handleNextFromTimeWindow = async () => {
    const currentTimeWindow = { complianceDays, billingWindow, customDays };
    const isUnchangedSinceLastFetch =
      fetchedTimeWindow !== null &&
      fetchedTimeWindow.complianceDays === complianceDays &&
      fetchedTimeWindow.billingWindow === billingWindow &&
      fetchedTimeWindow.customDays === customDays &&
      (statementPatients ?? []).length > 0;

    if (isUnchangedSinceLastFetch) {
      // Threshold and billing window are exactly what they were for the patient
      // list we already have — no need to hit the backend again.
      goNext();
      return;
    }

    setPatientsLoading(true);
    setPatientsError(null);
    try {
      const payload = buildInsuranceStatementPayload({
        billingWindow,
        complianceDays,
        customDays,
      });
      const { data } = await axios.post("/insurance-statement/patients", payload);

      // this hard coded data is for testing purposes only, remove it when the backend is ready dont remove
      const normalizedPatients = (data.data?? data.data).map((p) => ({
        id: p.patient_id,
        name: p.full_name,
        daysUsed: p.days_used_display,
        avatarUrl: p.avatar_url,
        usedDays: p.used_days,
        windowDays: p.window_days,
      }));

      if (normalizedPatients.length === 0) {
        setShowValidationError(
          "No patients meet the selected compliance threshold."
        );
        return;
      }

      setShowValidationError(null);
      setStatementPatients(normalizedPatients);
      setSelectedPatientIds(new Set());
      setFetchedTimeWindow(currentTimeWindow);
      // The compliance threshold and/or billing window actually changed, so any
      // previously-entered statement text no longer applies to this (new)
      // patient group — clear it and drop out of the finalized view.
      setStatementData({ condition: "", minutes: "", additionalMinutes: 0 });
      setIsStatementFinalized(false);
      setFinalizedPatientIds(null);
      goNext();
    } catch (error) {
      setPatientsError(handleApiError(error).message);
    } finally {
      setPatientsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          borderRadius: "6px",
          width: "910px",
          backgroundColor: "#ffffff",
          position: "relative",
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "24px 24px 0",
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        <ModalCloseButton onClick={handleClose} />

        <Typography
          style={{
            fontFamily: "Poppins",
            fontSize: "22px",
            fontWeight: 600,
            color: "#444444",
            textAlign: "center",
            lineHeight: "30px",
            marginTop: 20,
          }}
        >
          Insurance Statement
        </Typography>
      </DialogTitle>

      {showValidationError && (
        <div
          style={{
            position: "absolute",
            top: 90,
            right: 40,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            gap: 10,
            maxWidth: 420,
            backgroundColor: "#fff",
            borderRadius: 4,
            padding: "12px 20px",
            boxShadow: "0px 3px 6px rgba(0,0,0,0.15)",
          }}
        >
          <ValidationErrorIcon />
          <Typography style={{ fontFamily: "Poppins", fontSize: 16, color: "#444444" }}>
            {showValidationError}
          </Typography>
        </div>
      )}

      <DialogContent style={{ padding: "0 85px", overflowY: "auto" }}>
        {/* Breadcrumbs */}
        {!hideSteps && (
          <div
            className={styles.tab}
            style={{ width: "100%", marginTop: "30px", marginBottom: "2rem" }}
          >
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {STEPS.map((label, idx) => {
                const stepNum = idx + 1;
                const isActive = currentStep === stepNum;
                const isHovered = hoveredStep === stepNum;
                return (
                  <Typography
                    key={label}
                    className={styles.breadcrumbs}
                    onMouseEnter={() => setHoveredStep(stepNum)}
                    onMouseLeave={() => setHoveredStep(null)}
                    onClick={() => goToBreadcrumbStep(stepNum)}
                    style={{
                      position: "relative",
                      display: "inline-block",
                      fontSize: "16px",
                      fontFamily: "Poppins",
                      cursor: stepNum < currentStep ? "pointer" : "default",
                    }}
                  >
                    {/* Reserves layout width at the boldest (600) weight so the
                        visible text below never resizes its container on
                        hover/active-state changes, which was shifting the
                        separators and later steps sideways. */}
                    <span aria-hidden="true" style={{ visibility: "hidden", fontWeight: 600, display: "block" }}>
                      {label}
                    </span>
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        whiteSpace: "nowrap",
                        color: isActive ? "#197f8f" : "#313131",
                        fontWeight: isActive ? 600 : isHovered ? 500 : 400,
                        textDecoration: !isActive && isHovered ? "underline" : "none",
                        textUnderlineOffset: "3px",
                        transition:
                          "color 0.2s ease, font-weight 0.2s ease, text-decoration-color 0.2s ease",
                      }}
                    >
                      {label}
                    </span>
                  </Typography>
                );
              })}
            </Breadcrumbs>
            <div style={{ borderBottom: "1px solid #e0e0e0", marginTop: 8 }} />
          </div>
        )}

        <div style={{ minHeight: "546px", marginTop: hideSteps ? "2rem" : 0 }}>
          <StepWizard
            transitions={{}}
            isLazyMount={true}
            instance={getWizardInstance}
            initialStep={initialStep}
            onStepChange={({ activeStep }) => setCurrentStep(activeStep)}
          >
            <TimeWindowStep
              complianceDays={complianceDays}
              setComplianceDays={setComplianceDays}
              billingWindow={billingWindow}
              setBillingWindow={setBillingWindow}
              customDays={customDays}
              setCustomDays={setCustomDays}
            />
            <PatientsStep
              patients={statementPatients}
              selected={selectedPatientIds}
              onToggle={togglePatient}
              onToggleAll={toggleAllPatients}
            />
            <StatementStep
              patients={statementStepPatients}
              statementData={statementData}
              onUpdateField={updateStatementField}
              activeId={activeStatementPatientId}
              onActiveChange={handleActiveStatementPatientChange}
              isFinalized={isStatementFinalized && isStatementComplete}
              onEdit={() => setIsStatementFinalized(false)}
              hideSteps={hideSteps}
              complianceDays={complianceDays}
              showValidationError={showValidationError}
            />
          </StepWizard>
        </div>

        {(patientsError || summaryError) && (
          <Typography style={{ color: "#d32f2f", fontFamily: "Poppins", fontSize: 14, marginTop: 8 }}>
            {patientsError || summaryError}
          </Typography>
        )}
      </DialogContent>

      {/* Footer */}
      <div
        style={{
          padding: "32px 85px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {currentStep === 1 ? (
          <button
            onClick={handleClose}
            style={{
              ...footerBtnBase,
              border: "1px solid #197f8f",
              backgroundColor: "#fff",
              color: "#197f8f",
            }}
          >
            Cancel
          </button>
        ) : (
          <div style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
            {!hideSteps && (
              <button
                onClick={goBack}
                style={{
                  ...footerBtnBase,
                  border: "1px solid #197f8f",
                  backgroundColor: "#fff",
                  color: "#197f8f",
                }}
              >
                Back
              </button>
            )}

            <div style={{ position: "relative" }}>
              <button
                onClick={
                  currentStep < STEPS.length
                    ? goNext
                    : isStatementFinalized
                    ? undefined
                    : handleDoneAndCopy
                }
                disabled={
                  isNextFromPatientsDisabled ||
                  (currentStep === STEPS.length && (isStatementFinalized || isSubmittingSummary))
                }
                style={{
                  ...footerBtnBase,
                  border: "none",
                  backgroundColor: "#197f8f",
                  color: "#fff",
                  opacity:
                    isNextFromPatientsDisabled ||
                    (currentStep === STEPS.length && !isStatementFinalized && isSubmittingSummary)
                      ? 0.5
                      : 1,
                  cursor:
                    isNextFromPatientsDisabled ||
                    (currentStep === STEPS.length && !isStatementFinalized && isSubmittingSummary)
                      ? "not-allowed"
                      : currentStep === STEPS.length && isStatementFinalized
                      ? "default"
                      : "pointer",
                }}
              >
                {currentStep < STEPS.length
                  ? "Next"
                  : isStatementFinalized
                  ? "Copied to clipboard"
                  : isSubmittingSummary
                  ? "Submitting..."
                  : "Done & Copy"}
              </button>

              {currentStep === STEPS.length && isStatementFinalized && (
                <div className={statementStyles.copiedBadge} style={{ left: 167, top: -24 }}>
                  <div className={statementStyles.copiedBadgeCircle}>
                    <svg
                      className={statementStyles.copiedBadgeCheck}
                      width="18"
                      height="14"
                      viewBox="0 0 18 14"
                    >
                      <path d="M1 7L6.5 12.5L17 1.5" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <button
            onClick={handleNextFromTimeWindow}
            disabled={patientsLoading}
            style={{
              ...footerBtnBase,
              border: "none",
              backgroundColor: "#197f8f",
              color: "#fff",
              opacity: patientsLoading ? 0.7 : 1,
              cursor: patientsLoading ? "default" : "pointer",
            }}
          >
            {patientsLoading ? "Loading..." : "Next"}
          </button>
        )}
      </div>
    </Dialog>
  );
}
